import type { MeetingSamenvatting, MeetingStatus } from '@vip/shared';

/**
 * Hoe de stand van een gesprek op het scherm leest.
 *
 * Het statuswoord uit de database zegt de buddy niets — "EXTRACTED" is geen
 * antwoord op "kan ik verder?". Hier staat wat hij er wél van moet weten: hoe
 * ver het gesprek is, of hij aan zet is, en wat hij dan mag doen. Zowel de
 * pagina (hoeveel er op jou wachten) als de kaart leest hieruit, dus staat het
 * los van beide.
 */

/** Een gesprek doorloopt vier stappen. Dat is een echte volgorde, geen
 *  decoratieve nummering, dus die mag je zien: de buddy wil weten of hij aan
 *  zet is of moet wachten, en dat is met een statuswoord alleen niet af te
 *  lezen. */
export const STAPPEN = ['Opname', 'Transcript', 'Hulpvragen', 'Klaar'] as const;

export type Stand = 'klaar' | 'jij' | 'bezig' | 'open';

/** Hoe ver de keten is. */
const POSITIE: Record<MeetingStatus, number> = {
  UPLOADED: 0,
  TRANSCRIBING: 0,
  TRANSCRIBED: 1,
  CONFIRMED: 2,
  EXTRACTING: 2,
  EXTRACTED: 2,
  DONE: 4,
  FAILED: 0,
};

export const WACHT_OP_BUDDY: readonly MeetingStatus[] = ['TRANSCRIBED', 'EXTRACTED'];
export const IS_BEZIG: readonly MeetingStatus[] = ['TRANSCRIBING', 'EXTRACTING'];

/**
 * De stand zoals de buddy hem ziet. Na de upload en na het goedkeuren start de
 * server de volgende stap zelf, desnoods via de bewaker, dus voor de buddy is
 * die dan al bezig.
 */
export function weergave(g: Pick<MeetingSamenvatting, 'status' | 'heeftOpname'>): MeetingStatus {
  if (g.status === 'UPLOADED' && g.heeftOpname) return 'TRANSCRIBING';
  if (g.status === 'CONFIRMED') return 'EXTRACTING';
  return g.status;
}

/** Wat er bij één stap van het spoor te zien valt. Alleen de stap waar de buddy
 *  zélf iets moet doen valt op; de rest is stil. */
export function stand(status: MeetingStatus, stap: number): Stand {
  const hier = POSITIE[status];
  if (stap < hier) return 'klaar';
  if (stap > hier) return 'open';
  if (WACHT_OP_BUDDY.includes(status)) return 'jij';
  if (IS_BEZIG.includes(status)) return 'bezig';
  return 'open';
}

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive';

/** Eén regel die zegt wat de stand is. Precies één badge is gevuld: die van
 *  "jij bent aan zet". In een lijst van vijf gesprekken is dat de enige vraag
 *  die je hebt. */
export const STATUS_TEKST: Record<MeetingStatus, { label: string; variant: BadgeVariant }> = {
  UPLOADED: { label: 'Wacht op opname', variant: 'outline' },
  TRANSCRIBING: { label: 'Bezig met uitschrijven', variant: 'secondary' },
  TRANSCRIBED: { label: 'Jij bent aan zet', variant: 'default' },
  CONFIRMED: { label: 'Bezig met hulpvragen', variant: 'secondary' },
  EXTRACTING: { label: 'Bezig met hulpvragen', variant: 'secondary' },
  EXTRACTED: { label: 'Jij bent aan zet', variant: 'default' },
  DONE: { label: 'Klaar', variant: 'outline' },
  FAILED: { label: 'Mislukt', variant: 'destructive' },
};

/** Wat de buddy nu kan doen. Eén knop per gesprek: hij hoeft niet te weten hoe
 *  de pijplijn heet, alleen wat er van hem wordt gevraagd. */
export function actie(m: MeetingSamenvatting): { tekst: string; pad: string } | null {
  switch (m.status) {
    case 'TRANSCRIBED':
      return { tekst: 'Transcript controleren', pad: `/gesprekken/${m.id}/transcript` };
    case 'EXTRACTED':
      return { tekst: 'Hulpvragen beoordelen', pad: `/gesprekken/${m.id}/hulpvragen` };
    case 'DONE':
      return { tekst: 'Bekijken', pad: `/gesprekken/${m.id}/hulpvragen` };
    default:
      return null;
  }
}

/** Hoe lang het wachten duurt. Wachten zonder te weten hoe lang leest als een
 *  vastloper. */
export const WACHTTEKST: Partial<Record<MeetingStatus, string>> = {
  TRANSCRIBING: 'Duurt ongeveer een minuut.',
  EXTRACTING: 'Duurt een halve minuut.',
  UPLOADED: 'De opname is nog niet binnen.',
};

export const datum = (iso: string) =>
  new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });

const tijd = (iso: string) =>
  new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });

/**
 * De datum van het gesprek, met de tijd erbij als die erover gaat.
 *
 * `occurredAt` is de datum die de buddy zelf koos en komt uit een datumveld, dus
 * daar zit geen bruikbare tijd in. De tijd die er wél is, is die van aanmaken.
 * Twee gesprekken op dezelfde dag waren daardoor niet van elkaar te
 * onderscheiden, en ze stonden in willekeurige volgorde.
 *
 * De tijd komt er alleen bij als het gesprek op de dag zelf is aangemaakt. Kiest
 * iemand een datum van vorige week, dan hoort de klok van vandaag daar niet
 * achter: "12 augustus 2026 · 16:12" zou beweren dat het gesprek om die tijd
 * was, en dat is niet zo.
 */
export function wanneer(g: Pick<MeetingSamenvatting, 'occurredAt' | 'createdAt'>): string {
  const dag = datum(g.occurredAt);
  return dag === datum(g.createdAt) ? `${dag} · ${tijd(g.createdAt)}` : dag;
}
